import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Typography } from 'antd';
import * as React from 'react';

import {
  ActionKind,
  ActionParams,
  BaseClickable,
  SpacingRow,
  SpacingRowItem,
  SpacingUnit,
} from 'components/ui';
import { ActivityKind } from 'entities/TableCellActivity';
import { toDDMYYYYHHMMFormat } from 'utils/date';

import { ActivityRendererProps } from '../types';

import s from './Wrapper.module.scss';

type HeaderProps = React.PropsWithChildren<
  {
    icon?: React.ReactNode;
  } & ActivityRendererProps
>;

const Wrapper: React.FC<HeaderProps> = ({
  entity,
  icon,
  children,
  onEdit,
  onDelete,
}) => {
  const handleEdit = React.useCallback(() => {
    onEdit(entity);
  }, [entity, onEdit]);

  const handleDelete = React.useCallback(() => {
    onDelete(entity);
  }, [entity, onDelete]);

  const editAction: ActionParams = React.useMemo(
    () => ({
      actionKind: ActionKind.button,
      complexOnClick: {
        onClick: handleEdit,
      },
    }),
    [handleEdit]
  );

  const deleteAction: ActionParams = React.useMemo(
    () => ({
      actionKind: ActionKind.button,
      complexOnClick: {
        onClick: handleDelete,
      },
    }),
    [handleDelete]
  );

  return (
    <div className={s.wrapper}>
      <div className={s.wrapper__rightBlock}>
        <div className={s.wrapper__rightBlockHeader}>
          <div className={s.wrapper__rightBlockHeaderText}>
            {icon}
            <Typography.Title level={5}>
              {`${entity.author.firstName} ${entity.author.lastName}`}
            </Typography.Title>
          </div>
          <SpacingRowItem className={s.wrapper__rightBlockHeaderRight}>
            <div className={s.wrapper__rightBlockHeaderDate}>
              <Typography.Text type="secondary">
                {toDDMYYYYHHMMFormat(entity.createdAt)}
              </Typography.Text>
            </div>
            {entity.type === ActivityKind.comment && (
              <SpacingRow
                className={s.wrapper__rightBlockHeaderActions}
                hSpacing={SpacingUnit.unit4}
              >
                <BaseClickable action={editAction} withCursorPointer withHover>
                  <EditOutlined />
                </BaseClickable>
                <BaseClickable
                  action={deleteAction}
                  withCursorPointer
                  withHover
                >
                  <DeleteOutlined />
                </BaseClickable>
              </SpacingRow>
            )}
          </SpacingRowItem>
        </div>
        <div>{children}</div>
      </div>
    </div>
  );
};

export default Wrapper;
